import type { CallHistoryItem, CallSegmentType, CallStatus } from '../../types';
import { isSipOutboundSelfCall } from './outboundSelf';

export type CallDetailControlMode = 'default' | 'incoming' | 'outbound_self';

interface MonitorControlEligibilityInput {
  call: CallHistoryItem;
  controlMode: CallDetailControlMode;
  hasMonitorPermission: boolean;
  userId?: string | null;
  userExtension?: string | null;
}

const CALL_STATUS_RINGING = 'ringing';
const CALL_STATUS_IN_PROGRESS = 'in_progress';
const OUTBOUND_SELF_CONTROL_MODE: CallDetailControlMode = 'outbound_self';
const INCOMING_CONTROL_MODE: CallDetailControlMode = 'incoming';
const TRANSFER_DEST_TYPE_INTERNAL_EXTENSION = 'internal_extension';
const TRANSFER_DEST_TYPE_EXTERNAL_EXTENSION = 'external_extension';
const TELEPHONY_SEGMENT_INTERNAL_EXTENSION = 'telephony_internal_extension';
const TELEPHONY_SEGMENT_OUTBOUND_INTERNAL_EXTENSION = 'telephony_outbound_internal_extension';
const TELEPHONY_SEGMENT_EXTERNAL_EXTENSION = 'telephony_external_extension';

const MONITOR_BLOCKED_OWN_CALL_STATUSES = new Set<CallStatus>([
  CALL_STATUS_RINGING,
  CALL_STATUS_IN_PROGRESS,
]);

const USER_OWNED_EXTENSION_SEGMENT_TYPES = new Set<CallSegmentType>([
  TELEPHONY_SEGMENT_INTERNAL_EXTENSION,
  TELEPHONY_SEGMENT_OUTBOUND_INTERNAL_EXTENSION,
  TELEPHONY_SEGMENT_EXTERNAL_EXTENSION,
]);

function normalizeValue(value?: string | null): string {
  return String(value || '').trim();
}

/**
 * Returns whether the active media leg is already on an extension owned by the
 * current user. Future transfer metadata alone is not enough to block monitor:
 * AI and reverse IVR calls stay monitorable until the extension segment becomes
 * active.
 */
function isCurrentUserExtensionSegmentActive(
  call: CallHistoryItem,
  userId?: string | null,
  userExtension?: string | null,
): boolean {
  if (!call.activeSegmentType || !USER_OWNED_EXTENSION_SEGMENT_TYPES.has(call.activeSegmentType)) {
    return false;
  }

  const normalizedUserExtension = normalizeValue(userExtension);
  const normalizedDestinationValue = normalizeValue(call.transferDestinationValue);
  const normalizedAnsweredExtension = normalizeValue(call.interactionAnsweredExtension);
  const isExtensionDestinationType = (
    call.transferDestinationType === TRANSFER_DEST_TYPE_INTERNAL_EXTENSION
    || call.transferDestinationType === TRANSFER_DEST_TYPE_EXTERNAL_EXTENSION
  );
  const userOwnsDestinationExtension = Boolean(
    isExtensionDestinationType
    && normalizedDestinationValue
    && normalizedUserExtension
    && normalizedDestinationValue === normalizedUserExtension
  );
  const userOwnsTransferTarget = Boolean(
    userId
    && call.transferTargetUserId
    && String(call.transferTargetUserId) === String(userId)
  );
  const userAnsweredCurrentInteraction = Boolean(
    (userId && call.interactionAnsweredById && String(call.interactionAnsweredById) === String(userId))
    || (
      normalizedAnsweredExtension
      && normalizedUserExtension
      && normalizedAnsweredExtension === normalizedUserExtension
    )
  );

  return userOwnsDestinationExtension || userOwnsTransferTarget || userAnsweredCurrentInteraction;
}

/**
 * Decide if monitor control should be exposed for the current call context.
 *
 * Rules:
 * - Requires monitor permission.
 * - Never expose for outbound_self SIP calls (user is the participant).
 * - Hide monitor while the current user owns the active extension segment.
 */
export function canExposeMonitorControl({
  call,
  controlMode,
  hasMonitorPermission,
  userId,
  userExtension,
}: MonitorControlEligibilityInput): boolean {
  if (!hasMonitorPermission) {
    return false;
  }

  if (controlMode === OUTBOUND_SELF_CONTROL_MODE && isSipOutboundSelfCall(call)) {
    return false;
  }

  if (!MONITOR_BLOCKED_OWN_CALL_STATUSES.has(call.status)) {
    return true;
  }

  const callIsOwnedByCurrentUser = Boolean(
    controlMode === INCOMING_CONTROL_MODE
    || isCurrentUserExtensionSegmentActive(call, userId, userExtension)
  );

  return !callIsOwnedByCurrentUser;
}
